/**
 * M2 effects and world-view tuning (`src/data/m2fx.json`, M2 plan §2.9, owned by M2-20): the M2 particle pools'
 * caps, the emitter specs of every mining and salvage effect (the asset lane's `VfxEmitterSpec` shape), the beam
 * streaks, the tetherstone gate flicker strength, the holographic docking guide colours (style guide §3.6 E1
 * signal band) and the world-view constants (wreck damage look, ore chunk and fragment instancing).
 * Validated with Zod at load; the parsed object may be mutated by the dev panel.
 */
import { Color } from 'three/webgpu'
import { z } from 'zod'
import { FACTION_IDS, QUALITY_PRESETS, VFX_MATERIAL_KEYS, type FactionId, type QualityPreset, type VfxMaterialKey } from '../../render/contracts'
import m2fxJson from '../../data/m2fx.json'

const materialKey = z.enum(VFX_MATERIAL_KEYS as unknown as [VfxMaterialKey, ...VfxMaterialKey[]])
const presetKey = z.enum(QUALITY_PRESETS as unknown as [QualityPreset, ...QualityPreset[]])
const factionId = z.enum(FACTION_IDS as unknown as [FactionId, ...FactionId[]])
const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/)
const nonNeg = z.number().nonnegative()
const range = z.tuple([nonNeg, nonNeg]).refine((r) => r[0] <= r[1], 'min must be <= max')
const rgb = z.tuple([nonNeg, nonNeg, nonNeg])

/** The asset lane's `VfxEmitterSpec` (src/render/contracts.ts), validated like vfx.json. */
const emitterSchema = z.strictObject({
  material: materialKey,
  /** Particles per trigger; for the continuous effects (cutting sparks, molten edge, tractor motes): per second. */
  count: z.number().positive(),
  life: range,
  size: range,
  stretch: range,
  speed: range,
  coneDeg: z.number().min(0).max(180),
  inheritVelocity: z.number().min(0).max(1),
  drag: nonNeg,
  tint: rgb,
})
const recipe = z.array(emitterSchema).min(1)

/** One beam drawn as a direct streak instance per frame. */
const beamSchema = z.strictObject({
  /** The VFX pool (and so the core/halo colours) the streak draws with. */
  material: materialKey,
  tint: rgb,
  /** Streak width in metres at power 0 and 1 (the mining beam widens with laser power). */
  width: range,
  /** Normalized particle age the streak is drawn at: the VFX materials fade opacity by (1 − age). */
  age: z.number().min(0).max(0.99),
})

export const m2FxConfigSchema = z.strictObject({
  particles: z.strictObject({
    /** Total M2 particles (all pools) per quality preset. */
    caps: z.record(presetKey, z.number().int().min(1)),
    minPerPool: z.number().int().min(1),
    /** Share of the cap per pool; normalized. */
    weights: z.record(materialKey, z.number().positive()),
  }),
  effects: z.strictObject({
    chipSparks: recipe,
    rockBurst: recipe,
    fractureSuccess: recipe,
    anchorCut: recipe,
    cuttingSparks: recipe,
    moltenEdge: recipe,
    coolantVent: recipe,
    tractorMotes: recipe,
    captureGlint: recipe,
  }),
  beams: z.strictObject({ mining: beamSchema, cutting: beamSchema, tractor: beamSchema }),
  /** `tetherstone` event → `env.setGateFlicker(min(maxStrength, baseStrength + strengthPerTonne × tonnes))`. */
  gateFlicker: z.strictObject({ baseStrength: nonNeg, strengthPerTonne: nonNeg, maxStrength: z.number().positive().max(1) }),
  guides: z.strictObject({
    /** Tube radius of a corridor ring as a share of its radius (the rings frame the bay mouth). */
    ringTubeRatio: z.number().positive().max(0.5),
    /** Tube radius of a pad-target ring as a share of its radius. */
    padTubeRatio: z.number().positive().max(0.5),
    /** Torus radial and tubular segments. */
    ringSegments: z.tuple([z.number().int().min(3), z.number().int().min(8)]),
    okColor: hex,
    okIntensity: z.number().positive(),
    warnColor: hex,
    warnIntensity: z.number().positive(),
    padColor: hex,
    padIntensity: z.number().positive(),
    /** Radii (m) of the pad target's rings. */
    padRadius: z.array(z.number().positive()).min(1).max(4),
    stripColor: hex,
    stripIntensity: z.number().positive(),
    /** Cross-section (m) of the bay-edge light strips. */
    stripWidth: z.number().positive(),
  }),
  views: z.strictObject({
    /** `ShipLook.setDamage` hull fraction of a wreck (0 = destroyed). */
    wreckDamage: z.number().min(0).max(1),
    /** Livery of salvage part props. */
    partLook: factionId,
    chunk: z.strictObject({
      /** Chunk scale (the 1 m prop) per cube root of its tonnes. */
      scalePerCubeRootTonne: z.number().positive(),
      /** ± share of scale jitter per chunk seed. */
      scaleJitter: z.number().min(0).max(0.9),
      /** Spin rate range (rad/s) per chunk seed. */
      spinRate: range,
      roughness: z.number().min(0).max(1),
      metalness: z.number().min(0).max(1),
    }),
    fragments: z.strictObject({
      /** Asteroid-kit LOD drawn for fragments. */
      lod: z.number().int().min(0),
      /** Visual spin rate range (rad/s, `aSpin.w`). */
      spinRate: range,
    }),
  }),
})

export type M2FxConfig = z.infer<typeof m2FxConfigSchema>
export type M2EffectId = keyof M2FxConfig['effects']
export type BeamConfig = z.infer<typeof beamSchema>
export type GuideConfig = M2FxConfig['guides']
export type ViewsConfig = M2FxConfig['views']

/** Parses `m2fx.json` (or `raw`). Throws a ZodError naming the bad field. */
export function loadM2FxConfig(raw: unknown = m2fxJson): M2FxConfig {
  return m2FxConfigSchema.parse(raw)
}

/** Linear RGB of an sRGB hex colour times `intensity`, into `out` at `offset` (init only). */
export function linearColor(hexColor: string | number, intensity: number, out: Float32Array, offset: number): void {
  const c = new Color(hexColor)
  out[offset] = c.r * intensity
  out[offset + 1] = c.g * intensity
  out[offset + 2] = c.b * intensity
}
